import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-community/core'
import {
  getFilterOnCellContextMenu,
  getSelectedForContextMenu,
} from '@helios/ag-grid-config'
import {
  checkEveryExtTxIncludesAvailableAction,
  checkIsMatchAdditionalDisabled,
  checkIsMatchDisabled,
  checkIsMatchUsingExternalDisabled,
  checkSomeExtTxIncludesAvailableAction,
  ClearwaterTransaction,
  ExternalTransaction,
  ExternalTransactionAvailableActions,
  ExternalTransactionStatus,
  getMatchErrorMessage,
  getMatchUsingExternalErrorMessage,
  isManyToManyMatch,
} from '@helios/common-api'
import {
  getTransactionCategoryFromTypeKey,
  getTransactionTypeKeyFromCode,
} from '@helios/create-transactions-modal'
import { HighlightingService } from '@helios/generic-components'
import { ExternalTransactionsTaskService } from '../services/external-transactions-task.service'

/**
 * Get context menu for external transactions grid.
 */
export function getExternalTransactionsContextMenuItems(
  params: GetContextMenuItemsParams,
  cwTransSelected: ClearwaterTransaction[],
  taskService: ExternalTransactionsTaskService,
  highlightService: HighlightingService,
  hasCopilotPermissions: boolean,
  hasSecurityMappingPermissions: boolean,
  hasExternalFileViewerPermissions: boolean,
  isLIBSession = false,
): (string | MenuItemDef)[] {
  const menuItems: (string | MenuItemDef)[] = []

  let extTransSelected: ExternalTransaction[]
  if (params.context.isSelectAllRowsEnabled()) {
    extTransSelected = params.context.selectAllTransactionsData()
  } else {
    extTransSelected = getSelectedForContextMenu<ExternalTransaction>(params)
  }

  const extTran: ExternalTransaction = extTransSelected[0]

  // When no rows are selected, avoid showing menu items
  // to prevent us from getting into weird states.
  if (extTransSelected.length === 0) {
    return menuItems
  }
  const suffix =
    extTransSelected.length > 1 ? ' (' + extTransSelected.length + ')' : ''
  menuItems.push(
    ...createModalMenuItems(
      extTransSelected,
      taskService,
      suffix,
      isLIBSession,
    ),
    'separator',
    {
      name: 'Find in External File Viewer' + suffix,
      disabled:
        !hasExternalFileViewerPermissions || extTransSelected.length > 1,
      tooltip: !hasExternalFileViewerPermissions
        ? 'Feature not available due to lack of permission'
        : extTransSelected.length > 1
        ? 'Select a single transaction to view in raw data file'
        : 'View in raw data file',
      action: () => taskService.openExternalFileViewer(extTran),
    },
    {
      name: 'Create Security Mapping' + suffix,
      disabled: !hasSecurityMappingPermissions || extTransSelected.length > 1,
      tooltip: !hasSecurityMappingPermissions
        ? 'Feature not available due to lack of permission'
        : extTransSelected.length > 1
        ? 'Select a single transaction to view in security mapping'
        : 'Create security mapping',
      action: () => taskService.openSecurityMappingPanel(extTran),
    },
    ...getFilterOnCellContextMenu(params),
    'separator',
    {
      name: 'Find Matched Transactions' + suffix,
      disabled: !extTransSelected.find(
        row => row.status === ExternalTransactionStatus.MATCHED,
      ),
      tooltip: 'Filter the clearwater transactions by the matched transaction',
      action: () => taskService.findMatchedTransaction(extTransSelected),
    },
    ...createActionsContextMenuItems(
      extTransSelected,
      cwTransSelected,
      taskService,
      hasCopilotPermissions,
    ),
    'separator',
    ...highlightService.createHighlightMenuItems(params),
  )

  return menuItems
}

/**
 * Create transaction modal menu items
 */
function createModalMenuItems(
  extTransSelected: ExternalTransaction[],
  taskService: ExternalTransactionsTaskService,
  suffix?: string,
  isLIBSession = false,
): (string | MenuItemDef)[] {
  const extTran: ExternalTransaction = extTransSelected[0]
  const extTranId = `${extTran.extTranId}`
  const status = extTran.status
  const extTranIdBulk: number[] = []
  const bulkStatus: Map<number, ExternalTransactionStatus | undefined> =
    new Map()
  extTransSelected.forEach(extTrans => {
    extTranIdBulk.push(extTrans.extTranId)
    bulkStatus.set(extTrans.extTranId, extTrans.status)
  })
  const menuItems: ('separator' | MenuItemDef)[] = []
  const isLIBTran = isLIBTransaction(extTransSelected, isLIBSession)
  menuItems.push(
    {
      name: 'Post Cash Entry' + suffix,
      action: () => {
        extTransSelected.length == 1
          ? taskService.openCashEntryModal(extTranId, status)
          : taskService.openCashEntryBulkModal(extTranIdBulk, bulkStatus)
      },
      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    },
    {
      name: 'Post Trade' + suffix,
      action: () => {
        extTransSelected.length == 1
          ? taskService.openTradeEntryModal(extTranId, status)
          : taskService.openTradeEntryBulkModal(extTranIdBulk, bulkStatus)
      },
      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    },
    {
      name: 'Post FX Trade' + suffix,
      action: () => {
        extTransSelected.length == 1
          ? taskService.openFXTradeModal(extTranId, status)
          : taskService.openFxTradeBulkModal(extTranIdBulk, bulkStatus)
      },
      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    },
    {
      name: 'Post Currency Forward' + suffix,
      action: () => {
        extTransSelected.length == 1
          ? taskService.openCurrencyForwardModal(extTranId, status)
          : taskService.openCurrencyForwardBulkModal(extTranIdBulk, bulkStatus)
      },
      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    },
    {
      name: 'Post Wire Transfer' + suffix,
      action: () => {
        extTransSelected.length == 1
          ? taskService.openWireTransferModal(extTranId, status)
          : taskService.openWireTransferBulkModal(extTranIdBulk, bulkStatus)
      },
      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    },
  )

  if (extTransSelected.length > 1) {
    menuItems.push({
      name: 'Post Security Transfer In' + suffix,
      action: () =>
        taskService.openSecurityTransferInBulkModal(extTranIdBulk, bulkStatus),
      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    })
    menuItems.push({
      name: 'Post Security Transfer Out' + suffix,
      action: () =>
        taskService.openSecurityTransferOutBulkModal(extTranIdBulk, bulkStatus),
      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    })
  } else {
    menuItems.push({
      name: 'Post Security Transfer',
      action: () => taskService.openSecurityTransferModal(extTranId, status),

      disabled: shouldDisableAction(extTransSelected, 'post') || isLIBTran,
    })
  }
  return menuItems
}

/**
 * Create transaction actions context menu items
 */
function createActionsContextMenuItems(
  extTransSelected: ExternalTransaction[],
  cwTransSelected: ClearwaterTransaction[],
  taskService: ExternalTransactionsTaskService,
  hasCopilotPermissions: boolean,
): (string | MenuItemDef)[] {
  const suffix =
    extTransSelected.length > 1 ? ' (' + extTransSelected.length + ')' : ''
  const isAdditionalMatchDisabled = checkIsMatchAdditionalDisabled(
    cwTransSelected,
    extTransSelected,
  )
  const menuItems: Array<string | MenuItemDef> = [
    {
      name: 'Hold' + suffix,
      disabled: shouldDisableActionOnlyIfAllFails(extTransSelected, 'hold'),
      action: () => taskService.hold(extTransSelected),
    },
    {
      name: 'Unhold' + suffix,
      disabled: shouldDisableActionOnlyIfAllFails(extTransSelected, 'unhold'),
      action: () => taskService.unhold(extTransSelected),
    },
    {
      name: 'Ignore' + suffix,
      disabled: shouldDisableActionOnlyIfAllFails(extTransSelected, 'ignore'),
      action: () => taskService.ignore(extTransSelected),
    },
    {
      name: 'Unignore' + suffix,
      disabled: shouldDisableActionOnlyIfAllFails(extTransSelected, 'unignore'),
      action: () => taskService.unignore(extTransSelected),
    },
    {
      name: 'Unmatch' + suffix,
      disabled: checkIsUnmatchMenuItemDisabled(extTransSelected),
      action: () => taskService.unmatch(extTransSelected),
    },
    {
      name: 'Match' + (isAdditionalMatchDisabled ? '' : ' Additional') + suffix,
      disabled: isAdditionalMatchDisabled
        ? checkIsMatchDisabled(cwTransSelected, extTransSelected)
        : false,

      tooltip: isAdditionalMatchDisabled
        ? getMatchMenuItemTooltip(cwTransSelected, extTransSelected)
        : 'Match Additional matches to already matched transactions',
      action: () =>
        isManyToManyMatch(cwTransSelected, extTransSelected)
          ? taskService.manyToManyMatch(extTransSelected, cwTransSelected)
          : taskService.match(extTransSelected, cwTransSelected),
    },
    {
      name: 'Match Using External Data',
      disabled:
        !checkTransactionsCategory(cwTransSelected) ||
        checkIsMatchUsingExternalDisabled(cwTransSelected, extTransSelected),
      tooltip: getMatchUsingExternalMenuItemTooltip(
        cwTransSelected,
        extTransSelected,
      ),
      action: () => {
        taskService.matchUsingExternal(extTransSelected, cwTransSelected)
      },
    },
  ]
  if (
    hasCopilotPermissions &&
    extTransSelected.length == 1 &&
    (extTransSelected[0].status == ExternalTransactionStatus.UNMATCHED ||
      extTransSelected[0].status == ExternalTransactionStatus.HELD) &&
    window.copilot
  ) {
    menuItems.push({
      name: 'Handle with AI',
      icon: `<img src="${window.copilot.getIconUrl()}" alt="ClearwaterGPT" class="copilot-logo-context-menu"/>`,
      action: () => {
        window.copilot.getApplicationChatResponse(
          `What should I do with the external transaction id: ${extTransSelected[0].extTranId}`,
          { dsUsername: 'rubina' },
        )
        window.copilot.open()
      },
    })
  }
  return menuItems
}

/**
 * Function to check whether any transaction is LIB transaction
 */
export function isLIBTransaction(
  extTransSelected: ExternalTransaction[],
  isLIBSession = false,
): boolean {
  return isLIBSession && extTransSelected.some(tran => tran.basis)
}

/**
 * Check if action menu item should be disabled.
 */
export function shouldDisableAction(
  extTrans: ExternalTransaction[],
  action: ExternalTransactionAvailableActions,
): boolean {
  return !checkEveryExtTxIncludesAvailableAction(extTrans, action)
}
/**
 * Check if action menu item should be disabled.
 */
export function shouldDisableActionOnlyIfAllFails(
  extTrans: ExternalTransaction[],
  action: ExternalTransactionAvailableActions,
): boolean {
  return !checkSomeExtTxIncludesAvailableAction(extTrans, action)
}
/**
 * Check if the unmatch context menu option is disabled.
 */
export function checkIsUnmatchMenuItemDisabled(
  extTrans: ExternalTransaction[],
): boolean {
  return !checkSomeExtTxIncludesAvailableAction(extTrans, 'unmatch')
}

/**
 * Get tooltip string for match action
 */
export function getMatchMenuItemTooltip(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): string {
  const errorMsg = getMatchErrorMessage(cwTrans, extTrans)
  if (errorMsg !== undefined) {
    return errorMsg
  }

  if (cwTrans.length > 1) {
    return 'Match selected external transaction to selected transactions'
  }

  return 'Match selected external transactions to selected transaction'
}
/**
 * Check that all selected transaction is of same category  or not
 */
export function checkTransactionsCategory(
  selectedRows: ClearwaterTransaction[],
): boolean {
  if (selectedRows.length > 0) {
    let transType = getTransactionTypeKeyFromCode(selectedRows[0].tranTypeCode)
    const tranCategory = getTransactionCategoryFromTypeKey(transType)
    for (const row of selectedRows) {
      transType = getTransactionTypeKeyFromCode(row.tranTypeCode)
      if (tranCategory != getTransactionCategoryFromTypeKey(transType)) {
        return false
      }
    }
  }
  return true
}
/**
 * Get tooltip string for matchUsingExternal action
 */
export function getMatchUsingExternalMenuItemTooltip(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): string {
  const errorMsg = getMatchUsingExternalErrorMessage(cwTrans, extTrans)
  const isCwSameCategory = checkTransactionsCategory(cwTrans)
  if (!isCwSameCategory) {
    return 'All selected Clearwater Transaction should be of same category'
  }
  if (extTrans.length > 1) {
    return 'Select only one external transaction'
  }
  if (errorMsg !== undefined) {
    return errorMsg
  }
  return 'Match selected external transactions to selected clearwater transaction'
}
